/**
 * Offset merge for the messaging inbox's `messages` field.
 *
 * `keyArgs` omits limit/offset, so the dock's 20-row preview and the 50-row list
 * share one cache entry and pages accumulate. Apollo re-runs page 0 on dock mount
 * (`cache-and-network`), after a reply, and on the preview poll.
 *
 * A `NetworkStatus.refetch` (reply, live update) overwrites the field unless the
 * watch sets `refetchWritePolicy` to `INBOX_MESSAGES_REFETCH_WRITE_POLICY`.
 * Overwrite calls this merge with an empty existing page, which stores only the
 * incoming slice and drops the paged tail. Polls and cache-and-network mounts
 * already merge. `pageReachesEnd` does not: a full page (`itemCount >= limit`)
 * never truncates, even when that page is shorter than the cached list.
 *
 * Each write is authoritative for `[offset, offset + incoming.length)`. That
 * window is replaced by the incoming page. Cached rows after the window stay
 * (the paged tail). Cached rows inside the window that the incoming page does
 * not name are gone, so a thread deleted on the server leaves the inbox on the
 * next refetch of its page. Ids are deduped by first occurrence, so a thread
 * that moved to the top is not also left in the tail.
 *
 * A smaller `total`, or a cached list longer than `total`, does not drop the
 * tail. Leaving a thread goes through `omitInboxThread` /
 * `removeInboxThreadFromCache` only.
 *
 * A short page replaces from its offset when the server has nothing past it:
 * fewer items than `limit`, and `total` is absent or `offset + items.length`
 * reaches `total`. A short page whose `total` is still past that window keeps
 * the tail.
 */

/**
 * Pass as `refetchWritePolicy` on every `messages` watch.
 *
 * Apollo Client 3 writes a refetch with overwrite when this is unset, so
 * `mergeInboxMessages` never sees the paged tail.
 */
export const INBOX_MESSAGES_REFETCH_WRITE_POLICY = 'merge' as const;

export interface InboxWatchVariables {
  limit?: number | null;
  offset?: number | null;
  search?: string | null;
  channel?: string | null;
}

/**
 * A reply refetches every active `messages` watch. The 20-row preview and the
 * 50-row list share one cache entry (`keyArgs` omits limit). Writing the shorter
 * page first shifts indexes, and the longer page then drops the row that slid
 * into its window. Skip the shorter page-0 watch when a longer one for the same
 * channel and search is active. A preview-only dock still refetches.
 */
export function shouldRefetchInboxMessages(
  variables: InboxWatchVariables | null | undefined,
  active: readonly (InboxWatchVariables | null | undefined)[]
): boolean {
  const limit = typeof variables?.limit === 'number' ? variables.limit : undefined;
  const offset = variables?.offset ?? 0;
  if (limit == null || offset !== 0) return true;
  const channel = variables?.channel ?? null;
  const search = variables?.search ?? null;
  for (const other of active) {
    const otherLimit = typeof other?.limit === 'number' ? other.limit : undefined;
    if (otherLimit == null || otherLimit <= limit) continue;
    if ((other?.offset ?? 0) !== 0) continue;
    if ((other?.channel ?? null) !== channel) continue;
    if ((other?.search ?? null) !== search) continue;
    return false;
  }
  return true;
}

interface InboxRefetchQuery {
  options: { variables?: InboxWatchVariables | null };
  refetch: () => Promise<unknown>;
}

interface InboxRefetchClient {
  // Apollo's query types are generic. `include` is `'active'`, `'all'`, or an array
  // of documents. A bare document matches no watches.
  getObservableQueries(include: any): { values(): Iterable<InboxRefetchQuery> };
  refetchQueries(options: any): unknown;
}

/** Refetch `messages` watches, skipping a short page-0 when a longer one is active. */
export function refetchInboxMessages(client: InboxRefetchClient, query: unknown): void {
  const include = [query];
  const active = [...client.getObservableQueries(include).values()].map((oq) => oq.options.variables);
  void client.refetchQueries({
    include,
    onQueryUpdated(observableQuery: InboxRefetchQuery) {
      if (!shouldRefetchInboxMessages(observableQuery.options.variables, active)) return false;
      return observableQuery.refetch();
    },
  });
}

export interface InboxCacheRef {
  __ref?: string;
  id?: string | number | null;
}

export interface InboxPageArgs {
  offset?: number | null;
  limit?: number | null;
}

interface InboxPage<T> {
  items?: readonly T[] | null;
  total?: number | null;
}

/** Enough of Apollo's cache for a leave to strip one thread from every inbox list. */
export interface InboxThreadCache {
  modify(options: { fields: { messages: (existing: unknown) => unknown } }): void;
  identify(obj: { __typename: 'Message'; id: string }): string | undefined;
  evict(options: { id: string }): boolean;
  gc(): void;
}

function canonicalMessageId(id: string): string {
  return id.startsWith('Message:') ? id.slice('Message:'.length) : id;
}

function itemKey(item: InboxCacheRef | null | undefined): string | null {
  if (item == null) return null;
  const key = item.__ref ?? item.id;
  if (key == null) return null;
  return canonicalMessageId(String(key));
}

function refersToMessage(item: InboxCacheRef | null | undefined, messageId: string): boolean {
  const key = itemKey(item);
  if (key == null) return false;
  return key === canonicalMessageId(messageId);
}

/**
 * Remove one thread from a cached inbox page. Rows that were not that thread
 * stay, in the same order, and `total` drops by one so paging still has a
 * remainder. A list that does not contain the thread is returned unchanged.
 */
export function omitInboxThread<T extends InboxCacheRef, P extends InboxPage<T>>(
  existing: P | null | undefined,
  messageId: string | null | undefined
): P | null | undefined {
  if (existing == null || !messageId || !existing.items) return existing;
  let removed = false;
  const items: T[] = [];
  for (const item of existing.items) {
    if (refersToMessage(item, messageId)) {
      removed = true;
      continue;
    }
    items.push(item);
  }
  if (!removed) return existing;
  const total = typeof existing.total === 'number' ? Math.max(0, existing.total - 1) : existing.total;
  return { ...existing, items, total };
}

/**
 * On leave success, strip the thread from every cached `messages` list (the
 * full inbox and any search) and evict the normalized Message. The other
 * paged rows stay. A false `leaveMessage` must not call this.
 */
export function removeInboxThreadFromCache(cache: InboxThreadCache, messageId: string | null | undefined): void {
  if (!messageId) return;
  cache.modify({
    fields: {
      messages(existing) {
        if (existing == null || typeof existing !== 'object') return existing;
        return omitInboxThread(existing as InboxPage<InboxCacheRef>, messageId);
      },
    },
  });
  const cacheId = cache.identify({ __typename: 'Message', id: canonicalMessageId(messageId) });
  if (!cacheId) return;
  cache.evict({ id: cacheId });
  cache.gc();
}

function dedupeById<T extends InboxCacheRef>(items: readonly T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    const key = itemKey(item);
    if (key != null) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    out.push(item);
  }
  return out;
}

/**
 * The server returned the last page: fewer rows than requested, and nothing
 * claimed past them. A short page with a `total` still beyond this window is
 * a partial read, not the end of the inbox.
 */
function pageReachesEnd(
  offset: number,
  limit: number | undefined,
  itemCount: number,
  total: number | undefined
): boolean {
  if (limit == null || itemCount >= limit) return false;
  return total == null || offset + itemCount >= total;
}

export function mergeInboxMessages<T extends InboxCacheRef, P extends InboxPage<T>>(
  existing: P | null | undefined,
  incoming: P,
  args: InboxPageArgs | null | undefined
): P {
  const offset = args?.offset ?? 0;
  const limit = typeof args?.limit === 'number' ? args.limit : undefined;
  const incomingItems = [...(incoming.items ?? [])];
  const existingItems = existing?.items ? [...existing.items] : [];
  const incomingTotal = typeof incoming.total === 'number' ? incoming.total : undefined;
  const reachedEnd = pageReachesEnd(offset, limit, incomingItems.length, incomingTotal);

  if (existingItems.length === 0) {
    return { ...incoming, items: incomingItems.slice() };
  }

  // Replace [offset, offset + incoming.length). Rows past that window stay until
  // a short page that has reached the end of the list truncates them.
  const merged: T[] = existingItems.slice();
  for (let i = 0; i < incomingItems.length; i++) {
    const item = incomingItems[i];
    if (item !== undefined) {
      merged[offset + i] = item;
    }
  }
  if (reachedEnd) {
    merged.length = offset + incomingItems.length;
  }
  return { ...incoming, items: dedupeById(merged) };
}
